import { Reveal, SectionTag } from "@/components/Reveal";
import EditorialMarquee from "@/components/EditorialMarquee";
import Seo, { breadcrumbSchema, webPageSchema } from "@/components/Seo";

const CAFE_IMG = "/adda-pic.webp";
const POUR_IMG = "/pour-hero.webp";
const KULHAD_IMG = "/kulhads-many.webp";

const TIMELINE = [
  { year: "The Beginning", text: "One counter in Kammanahalli. One belief — a perfect cup of chai is a moment of warmth, a shared conversation, a comforting ritual." },
  { year: "The Craft", text: "Journeys through tea gardens. Lessons from seasoned experts. The delicate balance of flavours, the subtle nuance of each leaf, the precise art of the brew." },
  { year: "The Growth", text: "HRBR, Hegdenagar, Bhartiya City, Kasturi Nagar — five outlets, one unchanged recipe: same leaf, same simmer, same hand-thrown kulhad." },
  { year: "Today", text: "Packaged blends, a growing journal, and franchise partners carrying the kulhad to new cities. More than chai — an experience, at scale." },
];

const About = () => (
  <div data-testid="about-page">
    <Seo
      title="Our Story | Chai Times Bengaluru"
      description="Discover how Chai Times grew from one Kammanahalli chai counter into five Bengaluru kulhad chai cafés built around craft, conversation and adda culture."
      path="/about"
      image="/adda-pic.webp"
      schema={[
        webPageSchema({
          type: "AboutPage",
          name: "About Chai Times",
          description: "The story of Chai Times, from one Kammanahalli counter to five Bengaluru kulhad chai cafés.",
          path: "/about",
        }),
        breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Our Story", path: "/about" }]),
      ]}
    />
    <section className="bg-forest text-cream pt-36 md:pt-44 pb-20 relative overflow-hidden grain" data-testid="about-hero">
      <video
        src="/about-loop.mp4"
        poster="/about-poster.jpg"
        autoPlay
        muted
        loop
        playsInline
        className="absolute inset-0 w-full h-full object-cover opacity-20"
        data-testid="about-hero-video"
      />
      <div className="max-w-7xl mx-auto px-5 md:px-10 relative z-10">
        <Reveal>
          <SectionTag dark>Our Story</SectionTag>
          <h1 className="font-serif font-black text-5xl md:text-7xl tracking-tight mt-5 max-w-4xl leading-[0.98]">
            Steeped in history,<br /><span className="italic text-gold">brewed for today.</span>
          </h1>
        </Reveal>
      </div>
    </section>

    <EditorialMarquee text="Chai tastes best with Kulhad" className="bg-gold text-charcoal" />

    <section className="max-w-7xl mx-auto px-5 md:px-10 py-24 grid md:grid-cols-12 gap-12 items-start">
      <div className="md:col-span-5 md:sticky md:top-32">
        <Reveal>
          <div className="rounded-3xl overflow-hidden aspect-[4/5]">
            <img src={CAFE_IMG} alt="Chai Times café adda in Bengaluru" loading="lazy" className="w-full h-full object-cover" />
          </div>
        </Reveal>
      </div>
      <div className="md:col-span-7">
        <Reveal>
          <h2 className="font-serif font-bold text-3xl md:text-4xl mb-8">We brew delicious & aromatic chai</h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="text-charcoal/75 leading-relaxed text-lg dropcap">
            At Chai Times, we believe that a perfect cup of chai is more than just a beverage; it's an experience. A moment of warmth, a shared conversation, a comforting ritual. And for years, we've been dedicated to crafting that perfect experience, one kulhad at a time.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="text-charcoal/75 leading-relaxed mt-6">
            Our story is steeped in the rich history of tea craftsmanship. We understand the delicate balance of flavours, the subtle nuances of each leaf, and the precise art of brewing that transforms simple ingredients into something truly extraordinary. This deep understanding informs every step of our process — from selecting the finest tea leaves to the careful preparation of each kulhad.
          </p>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 gap-5">
          <Reveal delay={0.1}>
            <img src={POUR_IMG} alt="Slow-brewed chai being poured at Chai Times" loading="lazy" className="rounded-2xl aspect-[4/3] object-cover w-full" />
          </Reveal>
          <Reveal delay={0.2}>
            <img src={KULHAD_IMG} alt="Stacked hand-thrown kulhads at Chai Times Bengaluru" loading="lazy" className="rounded-2xl aspect-[4/3] object-cover w-full" />
          </Reveal>
        </div>
      </div>
    </section>

    <section className="bg-terracotta text-cream py-24" data-testid="timeline-section">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-cream/70">The Journey</p>
          <h2 className="font-serif font-black text-4xl md:text-5xl tracking-tight mt-4 mb-16">From one counter<br />to a city's ritual</h2>
        </Reveal>
        <div className="grid md:grid-cols-4 gap-10">
          {TIMELINE.map((t, i) => (
            <Reveal key={t.year} delay={i * 0.1}>
              <div className="border-t-2 border-cream/40 pt-6">
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-cream/70">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-serif font-bold text-2xl mt-3 mb-4 italic">{t.year}</h3>
                <p className="text-cream/80 text-sm leading-relaxed">{t.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  </div>
);

export default About;
